import axios from 'axios';

const api = axios.create({ baseURL: '/api' });

const mem = new Map();
let logoutTimer = null;

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err.response?.status;
    const skip = err.config?.skipAuthRedirect;
    /* 401 = no token; 403 = bad/expired JWT in this panel */
    if ((status === 401 || status === 403) && !skip) {
      if (!logoutTimer) {
        logoutTimer = setTimeout(() => {
          logoutTimer = null;
          localStorage.removeItem('token');
          mem.clear();
          try {
            Object.keys(sessionStorage)
              .filter((k) => k.startsWith('apex:'))
              .forEach((k) => sessionStorage.removeItem(k));
          } catch {
            /* ignore */
          }
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
        }, 80);
      }
    }
    return Promise.reject(err);
  }
);

/** Session + memory cache so tab switches don't flash empty lists. */
export function cacheGet(key, fallback) {
  if (mem.has(key)) return mem.get(key);
  try {
    const raw = sessionStorage.getItem(`apex:${key}`);
    if (raw != null) {
      const parsed = JSON.parse(raw);
      mem.set(key, parsed);
      return parsed;
    }
  } catch {
    /* ignore */
  }
  return fallback;
}

export function cacheSet(key, value) {
  mem.set(key, value);
  try {
    sessionStorage.setItem(`apex:${key}`, JSON.stringify(value));
  } catch {
    /* quota / private mode */
  }
}

/**
 * Read list fields from wrapped API objects.
 * Also accepts a bare array (some endpoints). Never treats a non-array object as a list.
 */
export function listFrom(data, ...keys) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  for (const key of keys) {
    const val = data[key];
    if (Array.isArray(val)) return val;
  }
  return [];
}

/** Safely parse JSON string fields (apps, filters_hit) into string arrays. */
export function parseJsonList(raw) {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
        if (parsed && typeof parsed === 'object') return Object.keys(parsed).filter(Boolean);
      } catch {
        /* fall through to comma split */
      }
    }
    return trimmed.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [String(raw)];
}

/** Coerce filter/query params to strings for axios. */
export function stringParams(obj) {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v === '' || v == null) return;
    out[k] = String(v);
  });
  return out;
}

export function maskToken(token) {
  if (!token) return '—';
  if (token.length <= 8) return '••••••••';
  return `${token.slice(0, 4)}••••${token.slice(-4)}`;
}

/**
 * Native browser download via ?token= — avoids axios blob hangs while Node
 * ZipCrypto blocks the event loop (toasts stuck on "Скачиваю…").
 * path: '/logs/1/export/zip' (api base /api is prefixed).
 */
export async function downloadApiBlob(path, filename = 'download.bin') {
  const token = localStorage.getItem('token') || '';
  if (!token) throw new Error('Нет сессии — перелогинься');
  const rel = path.startsWith('/api/') ? path : `/api${path.startsWith('/') ? path : `/${path}`}`;
  const u = new URL(rel, window.location.origin);
  u.searchParams.set('token', token);
  const isExe = /\.exe$/i.test(filename) || /\/builds\/\d+\/download/.test(path);
  const outName = isExe
    ? (/\.exe$/i.test(filename) ? filename : `${String(filename).replace(/\.\w+$/, '')}.exe`)
    : filename;
  let handle = null;
  if (isExe && window.showSaveFilePicker) {
    try {
      handle = await window.showSaveFilePicker({
        suggestedName: outName,
        id: 'apex-build-exe',
        startIn: 'downloads',
      });
    } catch (e) {
      if (e && e.name === 'AbortError') return 'abort';
      handle = null;
    }
  }
  if (isExe) u.searchParams.set('bin', '1');
  const r = await fetch(u.toString(), { credentials: 'same-origin', cache: 'no-store' });
  if (!r.ok) throw new Error('download http ' + r.status);
  const buf = await r.arrayBuffer();
  if (handle) {
    const w = await handle.createWritable();
    await w.write(buf);
    await w.close();
    return 'fsa';
  }
  const saveName = isExe ? outName.replace(/\.exe$/i, '') + '.bin' : outName;
  const blob = new Blob([buf], { type: 'application/octet-stream' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = saveName;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    try { a.remove(); } catch { /* ignore */ }
  }, 2000);
  return isExe ? 'bin' : 'blob';
}

/**
 * GET + cache. On failure keeps previous cached/state value (no empty flash).
 * opts: { cacheKey, listKeys: ['builds'], map: (data)=>any }
 */
export async function apiGetCached(path, opts = {}) {
  const { cacheKey, listKeys, map, params, config } = opts;
  const { data } = await api.get(path, { ...(config || {}), params });
  let value;
  if (typeof map === 'function') value = map(data);
  else if (listKeys?.length) value = listFrom(data, ...listKeys);
  else value = data;
  if (cacheKey) cacheSet(cacheKey, value);
  return value;
}

export default api;
